import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DynamicObjectType } from '../../../types/commons';
import { getAllApi } from 'api/crud';
import { Select, SelectProps } from 'antd';
import { Waypoint } from 'react-waypoint';
import { convertRequestParams } from '@redux/crudCreator/dataProvider';
import { TFilterRequest } from 'components/rest/Filter/FilterContent';
import { debounce, get, isArray, unionBy } from 'lodash';
import MultiSelect, { MultiSelectProps } from 'components/commons/MultiSelect';
import { useGetReferenceQuery } from '@redux/references/slice';
import { useRestContext } from '../RestInputContext';
import _ from 'lodash';

export interface InfinitySelectProps extends Pick<MultiSelectProps, 'onClose'> {
  model: string;
  keyProp?: string;
  valueProp?: string;
  labelProp?: string;
  childFormKey?: string;
  initFilters?: {
    join?: string[];
    orderBy?: string | string[];
    filters?: TFilterRequest[];
    search?: string;
    ignoreLangCode?: boolean;
    filter?: string[];
    limit?: number;
    rawQuery?: DynamicObjectType;
  };
  searchKey?: string;
  labelFormat?: (d: DynamicObjectType) => ReactNode;
  onChange?: (
    d: string | string[] | number | DynamicObjectType | DynamicObjectType[],
    r?: any,
  ) => void;
  type?: 'single' | 'array';
  selected?: string | string[] | number[];
  placeholder?: string;
  dependency?: string | number;
  showInSideInput?: boolean;
  isGetAllValue?: boolean;
  isTreeSelect?: boolean;
  childrenProp?: string;
  allowClear?: boolean;
  formKey?: string;
  parentProp?: string;
  mode?: 'multiple' | 'tags';
  name?: string;
  searchMoreKeys?: string[];
  formatSearch?: (value: string) => void;
  selectProps?: SelectProps;
  operator?: string;
  disabled?: boolean;
}

interface SelectedItem {
  value: string | number;
  label: ReactNode;
  children?: SelectedItem[];
}

export default function InfinitySelect({
  model,
  keyProp = 'id',
  valueProp = 'id',
  labelProp = 'name',
  initFilters,
  labelFormat,
  searchKey = 'name',
  type = 'single',
  onChange,
  selected,
  onClose,
  placeholder,
  dependency,
  showInSideInput,
  isGetAllValue,
  isTreeSelect,
  childrenProp,
  allowClear = true,
  parentProp,
  mode,
  name,
  searchMoreKeys,
  formatSearch,
  selectProps,
  operator,
  disabled,
}: InfinitySelectProps) {
  const { record } = useRestContext();
  const { data } = useGetReferenceQuery(
    {
      model,
      filters: {
        ...initFilters,
        limit:
          type === 'array' &&
          Array.isArray(selected) &&
          selected?.length >= 10 &&
          99,
        filters: [
          {
            condition: 'and',
            key: valueProp,
            operator: operator || 'is',
            value: Array.isArray(selected)
              ? selected?.map((e) =>
                  typeof e === 'string'
                    ? e
                    : e?.[valueProp]?.id || e?.[valueProp] || e?.id,
                )
              : selected,
          },
        ],
      },
    },
    {
      skip: !selected?.length,
    },
  );

  const params = useMemo(() => ({ page: 1, loading: false }), []);
  const [resourceData, setResourceData] = useState([]);
  const [endLoadMore, setEndLoadMore] = useState(false);
  const [filters, setFilters] = useState({
    filters: [],
    search: '',
    idFilterNotIsSelected: false,
    ...initFilters,
  });

  useEffect(() => {
    setFilters({
      filters: [],
      search: '',
      idFilterNotIsSelected: false,
      ...initFilters,
    });
  }, [dependency]);

  const setSearch = useCallback((str) => {
    setFilters((old) => {
      old.search = str;
      return { ...old };
    });
  }, []);

  const hanldeClear = useCallback(() => {
    setFilters({
      filters: [],
      search: '',
      idFilterNotIsSelected: false,
      ...initFilters,
    });
  }, []);

  const onSearchDebounce = useCallback(debounce(setSearch, 300), [setSearch]);

  const handleFilter = useMemo(() => {
    const searchDataKey = [];
    if (formatSearch && filters?.search !== '') {
      return formatSearch(filters?.search);
    }
    if (searchMoreKeys?.length) {
      searchMoreKeys.forEach((key) => {
        searchDataKey.push({
          key,
          value: filters?.search?.trim(),
          operator: operator || 'is',
          condition: 'or',
        });
      });
    }
    if (filters?.idFilterNotIsSelected && filters?.search !== '') {
      return [
        ...filters.filters,
        ...searchDataKey,
        !searchMoreKeys?.length && {
          condition: 'and',
          key: searchKey,
          operator: 'contL',
          value: filters?.search?.trim(),
        },
        {
          condition: 'and',
          key: keyProp,
          operator: 'notin',
          value: Array.isArray(selected)
            ? selected?.map((e) =>
                typeof e === 'string'
                  ? e
                  : e?.[valueProp]?.id || e?.[valueProp] || e?.id,
              )
            : selected,
        },
      ];
    }

    if (filters?.idFilterNotIsSelected) {
      return [
        ...filters.filters,
        {
          condition: 'and',
          key: keyProp,
          operator: 'notin',
          value: Array.isArray(selected)
            ? selected?.map((e) =>
                typeof e === 'string'
                  ? e
                  : e?.[valueProp]?.id || e?.[valueProp] || e?.id,
              )
            : selected,
        },
      ];
    }

    if (filters?.search !== '') {
      return [
        ...filters.filters,
        ...searchDataKey,
        !searchMoreKeys?.length && {
          condition: 'and',
          key: searchKey,
          operator: 'contL',
          value: filters?.search?.trim(),
        },
      ];
    }

    return filters?.filters;
  }, [filters, selected]);

  const onLoadMore = useCallback(() => {
    try {
      params.page += 1;
      const requestParams = convertRequestParams('GET_ALL', {
        ..._.omit(filters, ['idFilterNotIsSelected']),
        filters: handleFilter as TFilterRequest[],
        page: params.page,
        orderBy:
          Array?.isArray(filters?.orderBy) && filters?.orderBy?.length > 0
            ? JSON.parse(JSON.stringify(filters?.orderBy))
            : null,
      });
      !params.loading &&
        getAllApi(model, requestParams).then(({ data }) => {
          params.loading = false;
          if (data?.length > 0) {
            setResourceData((old) =>
              params.page === 1 ? data : [...old, ...data],
            );
          } else {
            setEndLoadMore(true);
          }
        });
      params.loading = true;
    } catch (error) {
      console.log(error);
    }
  }, [model, params, filters]);

  useEffect(() => {
    setEndLoadMore(false);
    params.loading = false;
    params.page = 1;
    setResourceData([]);

    const requestParams = convertRequestParams('GET_ALL', {
      ..._.omit(filters, ['idFilterNotIsSelected']),
      filters: handleFilter as TFilterRequest[],
      orderBy:
        Array?.isArray(filters?.orderBy) && filters?.orderBy?.length > 0
          ? JSON.parse(JSON.stringify(filters?.orderBy))
          : null,
    });
    const extract = async () => {
      try {
        !params.loading &&
          (await getAllApi(model, requestParams).then(({ data }) => {
            params.loading = false;
            setResourceData(() => data);
          }));
      } catch (error) {
        console.log(error);
      }
    };
    extract();
    params.loading = true;
  }, [filters, model]);

  const items = useMemo(() => {
    let convertResourceData = [];
    const mapItem = (item) => ({
      value: item?.[valueProp],
      item,
      label: labelFormat ? labelFormat(item) : String(get(item, labelProp)),
      children:
        isTreeSelect && item[childrenProp]
          ? item[childrenProp].map(mapItem)
          : null,
    });
    if (
      !isTreeSelect &&
      (isArray(get(record, name))
        ? get(record, name).length
        : get(record, name))
    ) {
      convertResourceData = unionBy(
        [...resourceData, ...(data || [])],
        'id',
      ).map(mapItem);
    } else {
      //TODO: fix bug when selected is array
      convertResourceData = unionBy(
        [...resourceData, ...(data || [])],
        'id',
      ).map(mapItem);
    }

    if (!endLoadMore && resourceData?.length >= (filters?.limit || 10)) {
      convertResourceData.push({
        disabled: true,
        value: 'loadingTracking',
        label: (
          <div>
            <Waypoint onEnter={onLoadMore} />
          </div>
        ),
        children: '',
      });
    }

    return convertResourceData;
  }, [resourceData, data, type, endLoadMore]);

  const selectedItems = useMemo<SelectedItem[]>(() => {
    if (!Array.isArray(selected)) return [];

    const getLabel = (item: DynamicObjectType | undefined): ReactNode => {
      if (!item) return 'Waiting update';
      if (labelFormat) {
        return labelFormat(item);
      } else {
        return String(get(item, labelProp) ?? item);
      }
    };

    const mapSelectedItems = (
      selectedItems: string[] | number[] | DynamicObjectType[],
    ): SelectedItem[] => {
      return selectedItems?.map((e) => {
        const value =
          typeof e === 'string' ? e : (e?.[valueProp] || e?.id) ?? '';
        return {
          value: value,
          label: getLabel(
            data?.find((item) => item?.[valueProp] === value) || e,
          ),
          children:
            isTreeSelect && e[childrenProp]
              ? mapSelectedItems(e[childrenProp])
              : null,
        };
      });
    };

    return mapSelectedItems(selected);
  }, [data, selected]);

  return type === 'array' ? (
    <MultiSelect
      onClose={onClose}
      handleChange={(e: string | string[]) => {
        if (isGetAllValue) {
          onChange(
            Array.isArray(e) &&
              e.map((id) => resourceData?.find((obj) => obj?.id === id) || id),
            resourceData.filter((item) => e?.includes?.(item[valueProp])),
          );
        } else {
          onChange(
            e,
            resourceData.find((item) => item[valueProp] === e),
          );
        }
      }}
      selected={selectedItems}
      options={items}
      placeholder={placeholder}
      showInSideInput={showInSideInput}
      isTreeSelect={isTreeSelect}
      parentProp={parentProp}
      selectedData={data}
      onSearch={onSearchDebounce}
      onFilterNotIsSelected={(status) =>
        setFilters((value) => ({ ...value, idFilterNotIsSelected: status }))
      }
      mode={mode}
      {...selectProps}
    />
  ) : (
    <Select
      showSearch
      onSearch={onSearchDebounce}
      options={items}
      filterOption={() => true}
      onSelect={(e) =>
        onChange(e, items.find((item) => e === item.value)?.item)
      }
      placeholder={placeholder}
      allowClear={allowClear}
      {...(allowClear && {
        onClear: () => {
          hanldeClear();
          onChange(null);
        },
      })}
      value={selected}
      className="w-full"
      disabled={disabled}
    />
  );
}
